import api from './api';

export type VideoPlaybackEventType =
  | 'playback_started'
  | 'stall_detected'
  | 'fallback_triggered'
  | 'fallback_failed'
  | 'playback_failed'
  | 'playback_recovered'
  | 'compat_missing';

interface ClientEvent {
  eventType: VideoPlaybackEventType;
  mediaId: string;
  sessionId: string;
  userId?: string;
  isPublic: boolean;
  reason?: 'stall' | 'error' | '';
  errorCode?: number;
  errorMessage?: string;
  positionSec?: number;
  stallDurationMs?: number;
  viaFallback?: boolean;
  screenW: number;
  screenH: number;
  networkType: string;
  downlinkMbps: number;
  // Negotiated HTTP version reported by PerformanceResourceTiming
  // (e.g. 'h2', 'h3', 'http/1.1'). Empty when not yet observable
  // (URL just resolved, video element hasn't fetched a byte).
  httpProtocol?: string;
  // Diagnostic snapshot of <video> state at emit time. Populated on failure
  // events; left at 0/empty otherwise. Helps distinguish a decoder that never
  // saw bytes (videoWidth=0, readyState=0) from one that produced frames and
  // then errored (videoWidth>0, readyState>=2).
  videoWidth?: number;
  videoHeight?: number;
  readyState?: number;
  networkState?: number;
  // Hostname of video.currentSrc — distinguishes CloudFront / compat-CDN /
  // EC2 proxy in the dashboard without parsing the URL there.
  currentSrcHost?: string;
  // 'proxy' or 'compat' when emitted from a fallback-related event.
  fallbackPath?: string;
  clientTs: string;
}

type RecordInput = Omit<
  ClientEvent,
  'sessionId' | 'clientTs' | 'screenW' | 'screenH' | 'networkType' | 'downlinkMbps'
>;

const SESSION_KEY = 'videoTelemetrySessionId';
const ENDPOINT = '/api/media/telemetry/video-events';
const API_PATH = '/media/telemetry/video-events';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 20;

function generateSessionId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 16; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
}

function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id || !/^[a-z0-9]{8,32}$/.test(id)) {
      id = generateSessionId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return generateSessionId();
  }
}

function getNetworkInfo(): { networkType: string; downlinkMbps: number } {
  try {
    const c = (navigator as any).connection;
    return {
      networkType: c?.effectiveType ? String(c.effectiveType) : '',
      downlinkMbps: typeof c?.downlink === 'number' ? c.downlink : 0,
    };
  } catch {
    return { networkType: '', downlinkMbps: 0 };
  }
}

function getScreen(): { screenW: number; screenH: number } {
  try {
    return {
      screenW: window.screen?.width || 0,
      screenH: window.screen?.height || 0,
    };
  } catch {
    return { screenW: 0, screenH: 0 };
  }
}

const queue: ClientEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const sessionId = getOrCreateSessionId();

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush(false);
  }, FLUSH_INTERVAL_MS);
}

async function flush(useBeacon: boolean): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.splice(0, MAX_BATCH);
  const body = { events: batch };

  try {
    if (useBeacon && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    await api.post(API_PATH, body);
  } catch {
    // Swallow: telemetry must never break playback.
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
  window.addEventListener('pagehide', () => void flush(true));
}

export const videoTelemetry = {
  sessionId,

  record(partial: RecordInput): void {
    try {
      queue.push({
        ...partial,
        sessionId,
        clientTs: new Date().toISOString(),
        ...getScreen(),
        ...getNetworkInfo(),
      });
      // playback_started is the denominator for every rate on the dashboard.
      // Don't risk losing it to the 5s batch window or an unmount-aborted XHR
      // -- flush it immediately while the page is still alive.
      if (partial.eventType === 'playback_started') {
        void flush(false);
        return;
      }
      if (queue.length >= MAX_BATCH) {
        void flush(false);
      } else {
        scheduleFlush();
      }
    } catch {
      // Never throw from telemetry.
    }
  },

  flushNow(useBeacon: boolean = true): void {
    void flush(useBeacon);
  },
};
